/**
 * What: Embedded Shopify support page for contextual TestFeed help requests.
 * Transport / data-flow: Posts merchant-visible support context to the Shopify Worker, which proxies it to TestFeed.
 *
 */
import type { useAppBridge } from '@shopify/app-bridge-react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { useState } from 'react'
import { describeErrorForDiagnostics, resolveMerchantErrorMessage } from '../merchantErrors'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { submitSupportTicket } from '../posthogSupport'
import { shopifyUiTracker, shopifyUiFailure } from '../shopifyUiTracking'
import { houseImagery } from './imagery/houseImagery'
import { FooterHelp, HelpModal, InfoButton, InfoPopover } from './shared/PageHelp'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const HELP_MODAL = 'support-help-modal'
const RUN_DETAILS_INFO = 'support-run-details-info'

type ShopifySupportPageProps = {
  shopify: ReturnType<typeof useAppBridge>
  setupId?: string
  packetId?: string
  runId?: string
  latestStepKey?: string
}

const supportCopy = shopifyComponentCopy.shopifySupportPage

function buildDiagnosticSuffix(
  input: Pick<ShopifySupportPageProps, 'setupId' | 'packetId' | 'runId' | 'latestStepKey'>,
) {
  const rows = [...diagnosticRows(input), { label: 'Page', value: globalThis.window?.location.pathname ?? '/support' }]
  return `\n\n---\n${rows.map((row) => `${row.label}: ${row.value}`).join('\n')}`
}

function diagnosticRows(input: Pick<ShopifySupportPageProps, 'setupId' | 'packetId' | 'runId' | 'latestStepKey'>) {
  const rows: Array<{ label: string; value?: string }> = [
    { label: supportCopy.setupId004, value: input.setupId },
    { label: supportCopy.packetId005, value: input.packetId },
    { label: supportCopy.runId006, value: input.runId },
    { label: supportCopy.latestStepKey007, value: input.latestStepKey },
  ]
  return rows.filter((row): row is { label: string; value: string } => Boolean(row.value))
}

/** Renders an in-app support request form with attached diagnostic context. */
export function ShopifySupportPage({ shopify, setupId, packetId, runId, latestStepKey }: ShopifySupportPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [supportRequestId, setSupportRequestId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [merchantEmail, setMerchantEmail] = useState('')
  const [messageError, setMessageError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const rows = diagnosticRows({ setupId, packetId, runId, latestStepKey })

  const submitSupportRequest = async () => {
    if (isSubmitting) return
    const trimmedMessage = message.trim()
    const trimmedEmail = merchantEmail.trim()
    const nextMessageError = trimmedMessage.length === 0 ? supportCopy.describeProblemRequired018 : null
    const nextEmailError =
      trimmedEmail.length === 0
        ? supportCopy.emailRequired019
        : !EMAIL_PATTERN.test(trimmedEmail)
          ? supportCopy.emailInvalid020
          : null
    setMessageError(nextMessageError)
    setEmailError(nextEmailError)
    const tracker = shopifyUiTracker(shopify)
    if (nextMessageError || nextEmailError) {
      tracker.blocked('submit-ticket', 'validation')
      return
    }

    const attempt = tracker.request('submit-ticket')
    setIsSubmitting(true)
    setError(null)
    try {
      const fullMessage = `${trimmedMessage}${buildDiagnosticSuffix({ setupId, packetId, runId, latestStepKey })}`
      const result = await submitSupportTicket(shopify, fullMessage, { email: trimmedEmail })
      tracker.completed(attempt)
      setSupportRequestId(result.supportRequestId)
      setMessage('')
      setMerchantEmail('')
      shopify.toast?.show(supportCopy.supportRequestSent011)
    } catch (e) {
      tracker.failed(attempt, shopifyUiFailure(e))
      console.error('Support request failed', describeErrorForDiagnostics(e))
      const errorMessage = resolveMerchantErrorMessage(
        e,
        shopifyComponentCopy.shopifyApiErrors.couldNotSendSupportRequest011,
      )
      setError(errorMessage)
      shopify.toast?.show(errorMessage, { isError: true })
      reportClientDiagnostic(shopify, { schema: 'shopify-support', paths: ['submit_failed'] })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <s-section padding="base">
      <s-stack direction="block" gap="large-200">
        <s-stack direction="inline" gap="base" alignItems="center">
          <s-avatar
            src={houseImagery.supportContact}
            initials="M"
            alt={shopifyComponentCopy.houseImagery.supportContactAlt}
            size="large-200"
          />
          <s-stack direction="block" gap="small-200">
            <s-text>{supportCopy.intro001}</s-text>
            <s-text color="subdued">{supportCopy.followUp002}</s-text>
          </s-stack>
        </s-stack>
        {rows.length > 0 ? (
          <s-stack direction="block" gap="small-200">
            <s-heading>{supportCopy.runDetails003}</s-heading>
            <InfoButton id={RUN_DETAILS_INFO} heading={supportCopy.runDetails003} />
            <s-stack direction="block" gap="small-100">
              {rows.map((row) => (
                <s-stack key={row.label} direction="inline" justifyContent="space-between" gap="small-200">
                  <s-text color="subdued">{row.label}</s-text>
                  <s-box maxInlineSize="100%">
                    <s-text type="strong">{row.value}</s-text>
                  </s-box>
                </s-stack>
              ))}
            </s-stack>
          </s-stack>
        ) : (
          <s-text color="subdued">{supportCopy.noRunContext008}</s-text>
        )}
        {rows.length > 0 && (
          <InfoPopover id={RUN_DETAILS_INFO} why={supportCopy.runDetailsInfoWhy} how={supportCopy.runDetailsInfoHow} />
        )}
        {supportRequestId && (
          <s-banner heading={supportCopy.supportRequestSent011} tone="success">
            <s-text>
              {supportCopy.requestReference013} {supportRequestId}
            </s-text>
          </s-banner>
        )}
        {error && (
          <s-banner heading={supportCopy.unableToSendRequest012} tone="critical">
            <s-text>{error}</s-text>
          </s-banner>
        )}
        <s-text-area
          label={supportCopy.describeProblemLabel014}
          placeholder={supportCopy.describeProblemPlaceholder015}
          value={message}
          onInput={(event) => setMessage(event.currentTarget.value)}
          rows={4}
          autocomplete="off"
          error={messageError ?? undefined}
        />
        <s-email-field
          label={supportCopy.emailLabel016}
          placeholder={supportCopy.emailPlaceholder017}
          value={merchantEmail}
          onInput={(event) => setMerchantEmail(event.currentTarget.value)}
          autocomplete="email"
          error={emailError ?? undefined}
        />
        <s-stack direction="inline">
          <s-button
            variant="primary"
            onClick={() => {
              void submitSupportRequest()
            }}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting ? supportCopy.sendingSupportRequest010 : supportCopy.sendSupportRequest009}
          </s-button>
        </s-stack>
      </s-stack>
      <FooterHelp modalId={HELP_MODAL} link="what gets sent" />
      <HelpModal id={HELP_MODAL} heading={supportCopy.helpModalHeading}>
        <s-paragraph>{supportCopy.helpModalBody1}</s-paragraph>
        <s-paragraph color="subdued">{supportCopy.helpModalBody2}</s-paragraph>
      </HelpModal>
    </s-section>
  )
}
